import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight, ShoppingBag } from "lucide-react";
import { Logo } from "./Logo";
import { SocialIcons } from "./Contact";

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/shop", label: "Shop the Hat", id: "shop" },
  { to: "/coming-soon", label: "Next Drop", id: "coming-soon" },
  { to: "/contact", label: "Contact", id: "contact" },
  { to: "/shipping", label: "Shipping & Returns", id: "shipping" },
  { to: "/admin", label: "X Check In", id: "admin" },
];

export const SideNav = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-40 flex items-center justify-between px-3 sm:px-5 py-3 pointer-events-none">
        <button
          data-testid="nav-logo-button"
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          className="pointer-events-auto group flex items-center gap-3 bg-white pl-2 pr-4 py-2 shadow-[6px_6px_0_#E10600] transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0"
        >
          <Logo compact />
          <span className="hidden sm:flex flex-col gap-1 ml-1" aria-hidden>
            <span className="block w-5 h-[2px] bg-[#0B0B0B] transition-transform duration-200 group-hover:translate-x-1" />
            <span className="block w-5 h-[2px] bg-[#E10600]" />
          </span>
        </button>
        <Link
          to="/shop"
          data-testid="header-shop-button"
          className="pointer-events-auto flex items-center gap-2 bg-[#E10600] text-white px-4 py-3 font-display uppercase text-sm sm:text-base tracking-wide shadow-[5px_5px_0_#0B0B0B] transition-colors duration-200 hover:bg-[#0B0B0B]"
        >
          <ShoppingBag className="w-4 h-4" /> Cop the hat
        </Link>
      </header>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="scrim"
              className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              data-testid="nav-overlay"
            />
            <motion.aside
              key="panel"
              data-testid="side-nav-panel"
              className="fixed left-0 top-0 bottom-0 z-50 w-[88vw] max-w-[440px] bg-[#E10600] text-white flex flex-col grain overflow-hidden"
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 260, damping: 32 }}
              data-lenis-prevent
            >
              <div className="relative z-10 flex items-center justify-between p-4 bg-white">
                <Logo compact />
                <button data-testid="nav-close-button" onClick={() => setOpen(false)} aria-label="Close menu" className="p-2 text-[#0B0B0B] transition-transform duration-200 hover:rotate-90">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <nav className="relative z-10 flex-1 flex flex-col justify-center px-6 sm:px-8 gap-1">
                {LINKS.map((l, i) => (
                  <motion.div key={l.to} initial={{ x: -40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.12 + i * 0.06 }}>
                    <NavLink
                      to={l.to}
                      end
                      data-testid={`nav-link-${l.id}`}
                      className={({ isActive }) =>
                        `group flex items-baseline gap-4 py-1.5 font-display uppercase text-4xl sm:text-5xl leading-[0.95] transition-colors duration-200 ${isActive ? "text-[#0B0B0B]" : "text-white hover:text-[#0B0B0B]"}`
                      }
                    >
                      <span className="font-mono-x text-xs tracking-widest opacity-80">0{i + 1}</span>
                      <span className="transition-transform duration-300 group-hover:translate-x-2">{l.label}</span>
                      <ArrowUpRight className="w-6 h-6 self-center opacity-0 -translate-x-2 transition-[opacity,transform] duration-300 group-hover:opacity-100 group-hover:translate-x-0" />
                    </NavLink>
                  </motion.div>
                ))}
              </nav>
              <div className="relative z-10 p-6 sm:p-8 border-t border-white/30 flex items-center justify-between gap-4">
                <span className="font-mono-x text-xs uppercase tracking-[0.2em]">#X · Party Lyfe</span>
                <SocialIcons prefix="nav" />
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
